import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MapPin, Phone, Store, User } from "lucide-react";
import { friendlyError } from "@/lib/errors";
import { formatDateTime, formatMoney } from "@/lib/format";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { Modal } from "@/components/ui/Modal";
import { toast } from "@/components/ui/toast";
import { ErrorState, Skeleton } from "@/components/ui/states";
import {
  cancelOrder,
  fetchOrder,
  fetchOrderHistory,
  fetchOrderItems,
  fetchOrderPayments,
  fetchPaymentMethods,
  registerPayment,
  transitionOrder,
} from "./api";
import {
  CANCELLABLE,
  CANCEL_REASONS,
  fulfillmentLabel,
  nextStatus,
  orderStatus,
  paymentStatus,
} from "./status";

export function OrderDetailPage() {
  const { id = "" } = useParams();
  const { org } = useActiveOrg();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [cancelOpen, setCancelOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);

  const order = useQuery({ queryKey: ["order", id], queryFn: () => fetchOrder(id) });
  const items = useQuery({ queryKey: ["order-items", id], queryFn: () => fetchOrderItems(id) });
  const history = useQuery({ queryKey: ["order-history", id], queryFn: () => fetchOrderHistory(id) });
  const payments = useQuery({ queryKey: ["order-payments", id], queryFn: () => fetchOrderPayments(id) });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["order", id] });
    void qc.invalidateQueries({ queryKey: ["order-history", id] });
    void qc.invalidateQueries({ queryKey: ["order-payments", id] });
    void qc.invalidateQueries({ queryKey: ["orders"] });
  };

  const advance = useMutation({
    mutationFn: (status: string) => transitionOrder(id, status),
    onSuccess: () => {
      toast.ok("Estado actualizado");
      refresh();
    },
    onError: (e) => toast.error(friendlyError(e)),
  });

  if (order.isPending) return <DetailSkeleton />;
  if (order.isError) {
    return <ErrorState message={friendlyError(order.error)} onRetry={() => void order.refetch()} />;
  }

  const o = order.data;
  const st = orderStatus(o.order_status, o.fulfillment_type);
  const pay = paymentStatus(o.payment_status);
  const next = nextStatus(o.order_status, o.fulfillment_type);
  const currency = o.currency;
  const tz = org?.timezone ?? "America/Lima";

  return (
    <div className="flex flex-col gap-3">
      {/* --------------------------------------------------------- cabecera */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Link
            to="/ventas"
            className="mt-0.5 grid size-7 place-items-center rounded-md text-ink-2 hover:bg-sunk"
            aria-label="Volver a pedidos"
          >
            <ArrowLeft className="size-4" />
          </Link>
          <div>
            <h1 className="tnum text-head font-semibold tracking-tight">{o.order_number}</h1>
            <p className="tnum text-xs text-ink-3">{formatDateTime(o.created_at, tz)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={pay.tone}>{pay.label}</StatusPill>
          <StatusPill tone={st.tone}>{st.label}</StatusPill>
          {next && (
            <Button
              variant="primary"
              size="sm"
              loading={advance.isPending}
              onClick={() => advance.mutate(next.status)}
            >
              {next.label}
            </Button>
          )}
          {CANCELLABLE.has(o.order_status) && (
            <Button variant="danger" size="sm" onClick={() => setCancelOpen(true)}>
              Cancelar
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-3">
          {/* ------------------------------------------------------ lineas */}
          <Panel title="Productos" bleed>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[420px] border-collapse text-ui">
                <thead>
                  <tr>
                    {["Producto", "Cant.", "Precio", "Total"].map((h, i) => (
                      <th
                        key={h}
                        className={`border-b border-line bg-sunk px-4 py-2.5 font-mono text-mark font-medium uppercase tracking-[0.1em] text-ink-3 ${i === 0 ? "text-left" : "text-right"}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(items.data ?? []).map((it) => (
                    <tr key={it.id} className="border-b border-line-2 last:border-0">
                      <td className="px-4 py-3">
                        <span className="font-medium">{it.product_name_snapshot}</span>
                        <span className="tnum block text-xs text-ink-3">
                          {it.variant_name_snapshot} · {it.sku_snapshot}
                        </span>
                      </td>
                      <td className="tnum px-4 py-3 text-right">{it.quantity}</td>
                      <td className="tnum px-4 py-3 text-right text-ink-2">
                        {formatMoney(it.unit_price, currency, { withSymbol: false })}
                      </td>
                      <td className="tnum px-4 py-3 text-right font-semibold">
                        {formatMoney(it.total, currency, { withSymbol: false })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <dl className="flex flex-col gap-1.5 border-t border-line-2 px-4 py-3 text-ui">
              <Row label="Subtotal" value={formatMoney(o.subtotal, currency)} />
              {Number(o.discount_total) > 0 && (
                <Row label="Descuento" value={`− ${formatMoney(o.discount_total, currency)}`} />
              )}
              <Row label="Reparto" value={formatMoney(o.shipping_total, currency)} />
              <Row label="Total" value={formatMoney(o.total, currency)} strong />
            </dl>
          </Panel>

          {/* ------------------------------------------------------- pagos */}
          <Panel
            title="Pagos"
            actions={
              o.payment_status !== "paid" &&
              o.order_status !== "cancelled" && (
                <Button size="sm" variant="secondary" onClick={() => setPayOpen(true)}>
                  Registrar cobro
                </Button>
              )
            }
          >
            {(payments.data ?? []).length === 0 ? (
              <p className="text-ui text-ink-3">Sin cobros registrados.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {(payments.data ?? []).map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3 text-ui">
                    <span>
                      <span className="font-medium capitalize">{p.payment_method_code}</span>
                      {p.reference && (
                        <span className="tnum block text-xs text-ink-3">Op. {p.reference}</span>
                      )}
                    </span>
                    <span className="flex items-center gap-2">
                      <StatusPill tone={p.status === "paid" ? "ok" : "warn"}>
                        {p.status === "paid" ? "Confirmado" : "Por verificar"}
                      </StatusPill>
                      <span className="tnum font-semibold">{formatMoney(p.amount, currency)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>

        {/* ------------------------------------------------------- lateral */}
        <div className="flex flex-col gap-3">
          <Panel title="Cliente">
            <div className="flex flex-col gap-2 text-ui">
              <p className="flex items-center gap-2">
                <User className="size-3.5 shrink-0 text-ink-3" />
                {o.customer_name_snapshot}
              </p>
              {o.customer_phone_snapshot && (
                <a
                  href={`https://wa.me/${o.customer_phone_snapshot.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-accent hover:underline"
                >
                  <Phone className="size-3.5 shrink-0" />
                  <span className="tnum">{o.customer_phone_snapshot}</span>
                </a>
              )}
            </div>
          </Panel>

          <Panel title={fulfillmentLabel(o.fulfillment_type)}>
            <div className="flex flex-col gap-2 text-ui">
              {o.fulfillment_type === "delivery" ? (
                <>
                  <p className="flex items-start gap-2">
                    <MapPin className="mt-0.5 size-3.5 shrink-0 text-ink-3" />
                    <span>
                      {o.address_line}
                      <span className="block text-ink-3">{o.district}</span>
                      {o.reference && (
                        <span className="block text-xs text-ink-3">Ref: {o.reference}</span>
                      )}
                    </span>
                  </p>
                  {o.recipient_name && (
                    <p className="text-ink-3">Recibe: {o.recipient_name}</p>
                  )}
                </>
              ) : (
                <p className="flex items-start gap-2">
                  <Store className="mt-0.5 size-3.5 shrink-0 text-ink-3" />
                  <span>{o.pickup_location ?? "Recojo en tienda"}</span>
                </p>
              )}
              {o.notes && (
                <p className="rounded-md bg-sunk px-2.5 py-2 text-meta text-ink-2">
                  «{o.notes}»
                </p>
              )}
            </div>
          </Panel>

          <Panel title="Historial">
            <ol className="flex flex-col gap-3">
              {(history.data ?? []).map((h) => {
                const label = orderStatus(h.status, o.fulfillment_type);
                return (
                  <li key={h.id} className="flex gap-2.5 text-ui">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-line" />
                    <span className="min-w-0">
                      <span className="font-medium">{label.label}</span>
                      {h.notes && <span className="block text-ink-3">{h.notes}</span>}
                      <span className="tnum block text-xs text-ink-3">
                        {formatDateTime(h.created_at, tz)}
                        {!h.is_public && " · interno"}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </Panel>
        </div>
      </div>

      <CancelDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        orderNumber={o.order_number}
        units={(items.data ?? []).reduce((n, it) => n + it.quantity, 0)}
        paidAmount={o.payment_status === "paid" ? formatMoney(o.total, currency) : null}
        onConfirm={async (reason) => {
          try {
            await cancelOrder(id, reason);
            toast.ok("Pedido cancelado y stock devuelto");
            setCancelOpen(false);
            refresh();
            void navigate("/ventas");
          } catch (e) {
            toast.error(friendlyError(e));
          }
        }}
      />

      <PaymentDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        orderId={id}
        organizationId={org?.organizationId ?? null}
        defaultAmount={o.total}
        currency={currency}
        onDone={() => {
          setPayOpen(false);
          refresh();
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ piezas */

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className={strong ? "font-semibold" : "text-ink-2"}>{label}</dt>
      <dd className={`tnum ${strong ? "font-semibold" : "text-ink-2"}`}>{value}</dd>
    </div>
  );
}

function CancelDialog({
  open,
  onClose,
  orderNumber,
  units,
  paidAmount,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  orderNumber: string;
  units: number;
  paidAmount: string | null;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState(CANCEL_REASONS[0]!);
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      open={open}
      onClose={onClose}
      tone="danger"
      title={`Cancelar pedido ${orderNumber}`}
      description={
        <>
          Se devolverán <strong className="text-ink">{units} unidades</strong> al almacén.
          {paidAmount && (
            <>
              {" "}El cobro de <strong className="text-ink">{paidAmount}</strong> quedará
              como reembolso pendiente: el dinero se devuelve fuera del sistema.
            </>
          )}
        </>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            size="sm"
            variant="danger"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              await onConfirm(reason);
              setBusy(false);
            }}
          >
            Cancelar pedido
          </Button>
        </>
      }
    >
      <label className="flex flex-col gap-1.5">
        <span className="text-meta font-medium">Motivo</span>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="rounded-md border border-line bg-surface px-3 py-2 text-ui"
        >
          {CANCEL_REASONS.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>
      </label>
    </Modal>
  );
}

function PaymentDialog({
  open,
  onClose,
  orderId,
  organizationId,
  defaultAmount,
  currency,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  orderId: string;
  organizationId: string | null;
  defaultAmount: string;
  currency: string;
  onDone: () => void;
}) {
  const methods = useQuery({
    queryKey: ["payment-methods", organizationId],
    queryFn: () => fetchPaymentMethods(organizationId!),
    enabled: Boolean(organizationId) && open,
  });

  const [method, setMethod] = useState("");
  const [amount, setAmount] = useState(defaultAmount);
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const list = methods.data ?? [];
  const selected = method || list[0]?.code || "";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Registrar cobro"
      description="Confirma el pago solo después de verlo en tu propia aplicación. Una captura de pantalla no es prueba de nada."
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            disabled={!selected || !amount}
            onClick={async () => {
              setBusy(true);
              try {
                await registerPayment({
                  orderId,
                  methodCode: selected,
                  amount,
                  status: "paid",
                  reference,
                });
                toast.ok("Cobro registrado");
                onDone();
              } catch (e) {
                toast.error(friendlyError(e));
              }
              setBusy(false);
            }}
          >
            Confirmar cobro
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium">Método</span>
          <select
            value={selected}
            onChange={(e) => setMethod(e.target.value)}
            className="rounded-md border border-line bg-surface px-3 py-2 text-ui"
          >
            {list.map((m) => (
              <option key={m.code} value={m.code}>
                {m.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium">Monto</span>
          <span className="flex">
            <span className="flex items-center rounded-l-md border border-r-0 border-line bg-sunk px-2.5 font-mono text-meta text-ink-3">
              {currency === "PEN" ? "S/" : currency}
            </span>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              className="tnum w-full rounded-r-md border border-line bg-surface px-3 py-2 text-right text-ui"
            />
          </span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-meta font-medium">Código de operación</span>
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Opcional"
            className="tnum rounded-md border border-line bg-surface px-3 py-2 text-ui"
          />
          <span className="text-xs text-ink-3">
            No se puede repetir en otro pedido: la base lo rechaza.
          </span>
        </label>
      </div>
    </Modal>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-6 w-40" />
      <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Skeleton className="h-64 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    </div>
  );
}
